import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  getDocDataSnap,
  setDocData,
  deleteDocById,
} from "../../../utils/firebase/Firebase.utils";
import { useSearchParams } from "react-router-dom";
import BasicInformation from "./BasicInformation";
import TenancyInformation from "./TenancyInformation";
import DocumentsTenants from "./DocumentsTenants";
import OccupantsInformationFullPage from "./OccupantsInformationFullPage";
import { IoArrowBack } from "react-icons/io5";
import { FaTrashAlt, FaUser, FaPhoneAlt } from "react-icons/fa";
import { MdOutlineMailOutline, MdOutlineMarkEmailUnread } from "react-icons/md";
import { BsCalendarCheck, BsFileEarmarkText } from "react-icons/bs";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { formatPhoneNumber } from "../../../utils/EleghoUtils";

const calculateCompletionPercentage = (data) => {
  const requiredFields = [
    "name",
    "email",
    "phoneNumber",
    "nationality",
    "address",
    "leaseStartDate",
    "leaseEndDate",
  ];

  const filledFields = requiredFields.filter(
    (field) => data[field]
    // && data[field].trim() !== ""
  );
  return Math.round((filledFields.length / requiredFields.length) * 100);
};

const CircularProgress = ({ percentage }) => {
  const radius = 16;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative w-12 h-12">
      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 40 40">
        {/* Background circle */}
        <circle
          className="text-gray-200"
          strokeWidth="4"
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx="20"
          cy="20"
        />
        {/* Progress circle */}
        <circle
          className={
            percentage >= 90
              ? "text-[#8FCD00]"
              : percentage >= 70
              ? "text-[#363062]"
              : percentage >= 50
              ? "text-yellow-500"
              : "text-red-500"
          }
          strokeWidth="4"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx="20"
          cy="20"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span
          className={`text-sm font-medium ${
            percentage >= 90
              ? "text-[#8FCD00]"
              : percentage >= 70
              ? "text-[#363062]"
              : percentage >= 50
              ? "text-yellow-500"
              : "text-red-500"
          }`}
        >
          {percentage}%
        </span>
      </div>
    </div>
  );
};

export default function TenantDetailsFullpage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const detail = searchParams.get("detail") || "Basic Information";
  const [activeLink, setActiveLink] = useState(detail);
  const [tenant, setTenant] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const currentUser = useSelector((state) => state.userObject);

  useEffect(() => {
    setSearchParams({ detail: activeLink });
  }, [activeLink, setSearchParams]);

  useEffect(() => {
    const fetchTenant = async () => {
      getDocDataSnap("tenants", id, (tenant) => {
        setTenant(tenant);
        setIsLoading(false);
      });
    };
    fetchTenant();
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#363062]"></div>
      </div>
    );
  }

  const links = [
    "Basic Information",
    "Tenancy Information",
    "Occupants",
    "Documents",
  ];

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this tenant?")) {
      await deleteDocById("tenants", id);
      navigate("/resident-management?link=Long+Term+Leases");
      toast.success("Tenant deleted successfully");
    }
  };

  const handleOpenEmailApp = () => {
    window.open(`mailto:${tenant.email}`);
  };

  const allowedAccess = ["admin", "super-admin"];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      {/* Header Section */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Link
            to="/resident-management?link=Long+Term+Leases"
            className="p-1.5 hover:bg-gray-50 rounded-lg transition-colors duration-200"
          >
            <IoArrowBack className="w-4 h-4 text-gray-500" />
          </Link>
          <div className="flex items-start flex-col">
            <h1 className="text-lg font-medium text-[#363062]">
              Tenant Details
            </h1>
            <p className="text-xs text-gray-500">
              View and manage tenant information
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {currentUser?.roles?.some((role) => allowedAccess.includes(role)) && (
            <button
              onClick={handleDelete}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 transition-colors duration-200"
              title="Delete Tenant"
            >
              <FaTrashAlt className="w-3.5 h-3.5 mr-1.5" />
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Profile Card */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-4">
        <div className="p-5">
          <div className="flex items-start gap-6">
            {/* Profile Picture Section */}
            <div className="shrink-0">
              <div className="w-20 h-20 rounded-full bg-[#363062]/10 flex items-center justify-center text-[#363062]">
                <FaUser className="w-10 h-10" />
              </div>
            </div>

            {/* Main Content Section */}
            <div className="flex-1 min-w-0">
              {/* Top Row: Name, Status, and Actions */}
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="text-base font-medium text-gray-900 truncate">
                      {tenant.name}
                    </h2>
                    <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#8FCD00]/10 rounded-full">
                      <BsCalendarCheck className="w-3.5 h-3.5 text-[#8FCD00]" />
                      <span className="text-xs font-medium text-[#8FCD00]">
                        Active Tenant
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={handleOpenEmailApp}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-[#363062] rounded-md hover:bg-[#363062]/90 transition-colors duration-200"
                  >
                    <MdOutlineMarkEmailUnread className="w-3.5 h-3.5 mr-1.5" />
                    Send Email
                  </button>
                </div>
              </div>

              {/* Contact Information */}
              <div className="mt-4 flex items-center justify-between">
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2 text-gray-600 bg-gray-50 px-3 py-1.5 rounded-md">
                    <FaPhoneAlt className="w-3 h-3 text-gray-400" />
                    <span className="text-xs font-medium">
                      {formatPhoneNumber
                        ? formatPhoneNumber(tenant.phoneNumber)
                        : tenant.phoneNumber}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 bg-gray-50 px-3 py-1.5 rounded-md">
                    <MdOutlineMailOutline className="w-4 h-4 text-gray-400" />
                    <span className="text-xs font-medium">{tenant.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 bg-gray-50 px-3 py-1.5 rounded-md">
                    <BsFileEarmarkText className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-xs font-medium">
                      Lease: {tenant.leaseStart} - {tenant.leaseEnd}
                    </span>
                  </div>
                </div>

                {/* Data Completion Status */}
                <div className="flex items-center gap-3 bg-gray-50 px-4 py-2 rounded-lg">
                  <CircularProgress
                    percentage={calculateCompletionPercentage(tenant)}
                  />
                  <div className="flex flex-col">
                    <span className="text-xs font-medium text-gray-900">
                      Profile Complete
                    </span>
                    <span className="text-xs text-gray-500">
                      {calculateCompletionPercentage(tenant)}% of fields filled
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200 mb-4">
        <nav className="-mb-px flex space-x-6 overflow-x-auto">
          {links.map((link, index) => (
            <button
              key={index}
              onClick={() => setActiveLink(link)}
              className={`
                whitespace-nowrap py-2.5 px-1 border-b-2 font-medium text-xs
                ${
                  activeLink === link
                    ? "border-[#363062] text-[#363062]"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }
              `}
            >
              {link}
            </button>
          ))}
        </nav>
      </div>

      {/* Content Section */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        {activeLink === "Basic Information" && (
          <BasicInformation tenant={tenant} />
        )}
        {activeLink === "Tenancy Information" && (
          <TenancyInformation tenant={tenant} />
        )}
        {activeLink === "Documents" && <DocumentsTenants tenant={tenant} />}
        {activeLink === "Occupants" && (
          <OccupantsInformationFullPage tenant={tenant} />
        )}
      </div>
    </div>
  );
}
